/**
 * 🚗 ADMIN VEHICLE SERVICE - Admin creates vehicles for hosts
 * Allows admins to add vehicles to any host's fleet
 */

import { getApiBaseUrl } from '@/config/environment';


export interface CreateVehicleForHostRequest {
  hostId: string;
  vehicle: {
    make: string;
    model: string;
    year: number;
    plateNumber: string;
    vin?: string;
    color: string;
    transmission: 'Automatic' | 'Manual';
    fuelType: 'Petrol' | 'Diesel' | 'Electric' | 'Hybrid';
    seatingCapacity: number;
    currentMarketValue?: number;
    description?: string;
    features?: string[];
    type?: 'sedan' | 'suv' | 'truck' | 'van' | 'coupe' | 'convertible';
    state?: string;
    city?: string;
  };
  pricing: {
    selfDrive: number | null;
    withDriver: number | null;
    halfDaySelfDrive?: number | null;
    halfDayWithDriver?: number | null;
    fuelPriceFullTank?: number | null;
    currency: string; // ISO 4217 code: NGN, KES, GHS, ZAR, USD, EUR, GBP
  };
  insurance: {
    type: string;
    paymentTerm: string;
    hasGPS: boolean;
    hasInspectionCertificate: boolean;
  };
  availability?: {
    instantBooking?: boolean;
  };
}

export interface CreateVehicleResponse {
  success: boolean;
  message?: string;
  data?: {
    vehicleId: string;
    hostId: string;
    vehicle: any;
    pricing: any;
    insurance: any;
    status: string;
    isActive: boolean;
    createdAt: string;
  };
  error?: string;
}

export const adminVehicleService = {
  /**
   * 🚗 Admin creates a vehicle for a specific host
   */
  async createVehicleForHost(data: CreateVehicleForHostRequest): Promise<CreateVehicleResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/vehicles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create Vehicle API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to create vehicle (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Vehicle created successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error creating vehicle:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create vehicle'
      };
    }
  },

  /**
   * 📸 Admin uploads photos for a vehicle
   */
  async uploadVehiclePhotos(
    vehicleId: string,
    photos: { imageData: string; category?: string; isPrimary?: boolean }[]
  ): Promise<{ success: boolean; message?: string; error?: string; data?: any }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/vehicles/${vehicleId}/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          photos: photos.map(p => ({
            imageData: p.imageData,
            category: p.category || 'general',
            isPrimary: p.isPrimary || false
          }))
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Upload Photos API Error ${response.status}:`, errorText);

        let errorMessage = `Failed to upload photos (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();

      return {
        success: true,
        message: result.message || 'Photos uploaded successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error uploading photos:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to upload photos'
      };
    }
  }
};
